import React, { useEffect, useMemo, useState } from 'react';
import './AdminEmailManagement.css';

const emptyCompose = {
  from: '',
  to: '',
  cc: '',
  bcc: '',
  subject: '',
  body: '',
  templateKey: '',
  signatureId: '',
  type: 'compose',
  ctaText: '',
  ctaUrl: '',
  sendAt: '',
  groups: []
};

const AdminEmailManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [emailConfig, setEmailConfig] = useState(null);
  const [smtpConfig, setSmtpConfig] = useState({ host: '', port: 587, secure: false, user: '', pass: '', from: '', fromName: '' });
  const [sendgridConfig, setSendgridConfig] = useState({ apiKey: '', enabled: false });
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [mailbox, setMailbox] = useState([]);
  const [recipients, setRecipients] = useState({ people: [], groups: {}, classes: [] });
  const [templates, setTemplates] = useState({});
  const [signatures, setSignatures] = useState([]);
  const [suppression, setSuppression] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [dnsHealth, setDnsHealth] = useState(null);
  const [compose, setCompose] = useState(emptyCompose);
  const [bulk, setBulk] = useState({ ...emptyCompose, type: 'announcement' });
  const [accountForm, setAccountForm] = useState({ ownerType: 'student', ownerId: '', displayName: '', localPart: '', dashboardAccess: true });
  const [contactForm, setContactForm] = useState({ name: '', email: '', type: 'external', className: '' });
  const [signatureForm, setSignatureForm] = useState({ name: 'Creative Town School', body: 'Thank you,\nCreative Town School', defaultSignature: true });
  const [suppressForm, setSuppressForm] = useState({ email: '', reason: 'manual' });
  const [folder, setFolder] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedMail, setSelectedMail] = useState(null);
  const [files, setFiles] = useState([]);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  const senderOptions = useMemo(() => {
    const base = smtpConfig.from ? [{ email: smtpConfig.from, displayName: smtpConfig.fromName || smtpConfig.from }] : [];
    return [...base, ...accounts.filter(account => account.active !== false)];
  }, [accounts, smtpConfig]);

  const groupOptions = useMemo(() => Object.keys(recipients.groups || {}).sort(), [recipients]);
  const json = response => response.json().catch(() => ({}));
  const show = message => setNotice(message);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    await Promise.all([
      loadConfig(),
      loadStats(),
      loadAccounts(),
      loadRecipients(),
      loadMailbox(),
      loadLogs(),
      loadAudit(),
      loadSignatures(),
      loadSuppression(),
      loadScheduled()
    ]);
  };

  const loadConfig = async () => {
    const data = await fetch('/api/admin/email/config').then(json).catch(() => null);
    if (!data) return;
    setEmailConfig(data);
    setSmtpConfig({
      host: data.smtp?.host || '',
      port: data.smtp?.port || 587,
      secure: Boolean(data.smtp?.secure),
      user: data.smtp?.user || '',
      pass: '',
      from: data.smtp?.from || '',
      fromName: data.smtp?.fromName || ''
    });
    setSendgridConfig({ apiKey: '', enabled: Boolean(data.sendgrid?.enabled) });
    setTemplates(data.templates || {});
    if (data.smtp?.from) {
      setCompose(previous => previous.from ? previous : { ...previous, from: data.smtp.from });
      setBulk(previous => previous.from ? previous : { ...previous, from: data.smtp.from });
    }
  };

  const loadStats = () => fetch('/api/admin/email/stats').then(json).then(setStats).catch(() => {});
  const loadAccounts = () => fetch('/api/admin/email/accounts').then(json).then(data => setAccounts(Array.isArray(data) ? data : [])).catch(() => {});
  const loadRecipients = () => fetch('/api/admin/email/recipients').then(json).then(setRecipients).catch(() => {});
  const loadLogs = () => fetch('/api/admin/email/logs?limit=100').then(json).then(data => setLogs(Array.isArray(data) ? data : [])).catch(() => {});
  const loadAudit = () => fetch('/api/admin/email/audit?limit=100').then(json).then(data => setAudit(Array.isArray(data) ? data : [])).catch(() => {});
  const loadSignatures = () => fetch('/api/admin/email/signatures').then(json).then(data => setSignatures(Array.isArray(data) ? data : [])).catch(() => {});
  const loadSuppression = () => fetch('/api/admin/email/suppression').then(json).then(data => setSuppression(Array.isArray(data) ? data : [])).catch(() => {});
  const loadScheduled = () => fetch('/api/admin/email/scheduled').then(json).then(data => setScheduled(Array.isArray(data) ? data : [])).catch(() => {});

  const loadMailbox = (nextFolder = folder, nextSearch = search) => {
    const params = new URLSearchParams({ direction: nextFolder, search: nextSearch });
    return fetch(`/api/admin/email/mailbox?${params}`)
      .then(json)
      .then(data => setMailbox(Array.isArray(data) ? data : []))
      .catch(() => setMailbox([]));
  };

  const formDataFor = (payload, uploadFiles) => {
    const data = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (key === 'groups') data.append(key, JSON.stringify(value || []));
      else if (value !== undefined && value !== null) data.append(key, value);
    });
    uploadFiles.forEach(file => data.append('attachments', file));
    return data;
  };

  const saveSmtp = async () => {
    setLoading(true);
    const response = await fetch('/api/admin/email/config/smtp', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(smtpConfig)
    });
    const data = await json(response);
    show(data.message || data.error || 'SMTP updated');
    setLoading(false);
    loadConfig();
  };

  const saveSendgrid = async () => {
    const response = await fetch('/api/admin/email/config/sendgrid', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sendgridConfig)
    });
    const data = await json(response);
    show(data.message || data.error || 'Provider updated');
    loadConfig();
  };

  const sendCompose = async (mode = 'send') => {
    setLoading(true);
    const endpoint = mode === 'draft' ? '/api/admin/email/drafts' : mode === 'schedule' ? '/api/admin/email/scheduled' : '/api/admin/email/compose';
    const response = await fetch(endpoint, {
      method: 'POST',
      body: mode === 'schedule' ? JSON.stringify(compose) : formDataFor(compose, files),
      headers: mode === 'schedule' ? { 'Content-Type': 'application/json' } : undefined
    });
    const data = await json(response);
    show(data.message || data.error || 'Mail processed');
    setLoading(false);
    if (response.ok && mode === 'send') {
      setCompose(previous => ({ ...emptyCompose, from: previous.from }));
      setFiles([]);
    }
    loadMailbox();
    loadStats();
    loadLogs();
    loadAudit();
    loadScheduled();
  };

  const sendBulk = async (mode = 'send') => {
    setLoading(true);
    const payload = { ...bulk, recipients: bulk.to, groups: bulk.groups };
    const response = await fetch(mode === 'schedule' ? '/api/admin/email/scheduled' : '/api/admin/email/compose', {
      method: 'POST',
      body: mode === 'schedule' ? JSON.stringify(payload) : formDataFor(payload, bulkFiles),
      headers: mode === 'schedule' ? { 'Content-Type': 'application/json' } : undefined
    });
    const data = await json(response);
    show(data.message || data.error || 'Bulk mail processed');
    setLoading(false);
    loadMailbox();
    loadStats();
    loadLogs();
    loadAudit();
    loadScheduled();
  };

  const saveTemplates = async () => {
    const response = await fetch('/api/admin/email/templates', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(templates)
    });
    const data = await json(response);
    show(data.message || data.error || 'Templates saved');
    loadConfig();
  };

  const createAccount = async () => {
    const response = await fetch('/api/admin/email/accounts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(accountForm)
    });
    const data = await json(response);
    show(data.message || data.error || 'Account saved');
    if (response.ok) setAccountForm({ ownerType: 'student', ownerId: '', displayName: '', localPart: '', dashboardAccess: true });
    loadAccounts();
    loadRecipients();
  };

  const updateAccount = async (account, updates) => {
    const response = await fetch(`/api/admin/email/accounts/${account.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    const data = await json(response);
    show(data.message || data.error || 'Account updated');
    loadAccounts();
  };

  const deleteAccount = async account => {
    if (!window.confirm(`Delete ${account.email}?`)) return;
    const response = await fetch(`/api/admin/email/accounts/${account.id}`, { method: 'DELETE' });
    const data = await json(response);
    show(data.message || data.error || 'Account deleted');
    loadAccounts();
  };

  const saveContact = async () => {
    const response = await fetch('/api/admin/email/contacts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(contactForm)
    });
    const data = await json(response);
    show(data.message || data.error || 'Contact saved');
    if (response.ok) setContactForm({ name: '', email: '', type: 'external', className: '' });
    loadRecipients();
  };

  const saveSignature = async () => {
    const response = await fetch('/api/admin/email/signatures', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(signatureForm)
    });
    const data = await json(response);
    show(data.message || data.error || 'Signature saved');
    loadSignatures();
  };

  const deleteSignature = async id => {
    const response = await fetch(`/api/admin/email/signatures/${id}`, { method: 'DELETE' });
    const data = await json(response);
    show(data.message || data.error || 'Signature deleted');
    loadSignatures();
  };

  const addSuppression = async () => {
    const response = await fetch('/api/admin/email/suppression', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(suppressForm)
    });
    const data = await json(response);
    show(data.message || data.error || 'Suppression list updated');
    loadSuppression();
  };

  const removeSuppression = async email => {
    const response = await fetch(`/api/admin/email/suppression/${encodeURIComponent(email)}`, { method: 'DELETE' });
    const data = await json(response);
    show(data.message || data.error || 'Suppression entry removed');
    loadSuppression();
  };

  const runDnsHealth = async () => {
    setDnsHealth(null);
    const data = await fetch('/api/admin/email/dns-health').then(json);
    setDnsHealth(data);
  };

  const updateMail = async (mail, updates) => {
    const response = await fetch(`/api/admin/email/mailbox/${mail.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    const data = await json(response);
    show(data.message || data.error || 'Mail updated');
    loadMailbox();
  };

  const cancelScheduled = async item => {
    const response = await fetch(`/api/admin/email/scheduled/${item.id}`, { method: 'DELETE' });
    const data = await json(response);
    show(data.message || data.error || 'Schedule cancelled');
    loadScheduled();
  };

  const recipientCount = model => {
    const groupEmails = (model.groups || []).flatMap(group => recipients.groups?.[group] || []);
    const explicit = String(model.to || '').split(/[,;\n]/).map(item => item.trim()).filter(Boolean);
    return new Set([...groupEmails, ...explicit]).size;
  };

  return (
    <div className="admin-email-management">
      <div className="mail-header">
        <div>
          <p>Creative Town Mail</p>
          <h2>Email Management</h2>
        </div>
        <div className="mail-header-actions">
          <a href="/api/admin/email/export.csv">Export CSV</a>
          <button onClick={runDnsHealth}>Check DNS</button>
          <button onClick={loadAll}>Refresh</button>
        </div>
      </div>

      {notice && (
        <div className={`message ${notice.toLowerCase().includes('error') || notice.toLowerCase().includes('failed') ? 'error' : 'success'}`}>
          <span>{notice}</span>
          <button onClick={() => setNotice('')}>x</button>
        </div>
      )}

      <div className="email-stats">
        <div className="stat-card"><span className="stat-number">{stats?.total || 0}</span><span className="stat-label">Logged</span></div>
        <div className="stat-card success"><span className="stat-number">{stats?.successful || 0}</span><span className="stat-label">Successful</span></div>
        <div className="stat-card error"><span className="stat-number">{stats?.failed || 0}</span><span className="stat-label">Failed</span></div>
        <div className="stat-card"><span className="stat-number">{mailbox.length}</span><span className="stat-label">Mailbox</span></div>
        <div className="stat-card"><span className="stat-number">{scheduled.filter(item => item.status === 'scheduled').length}</span><span className="stat-label">Scheduled</span></div>
      </div>

      <div className="email-tabs">
        {['overview', 'compose', 'mailbox', 'bulk', 'templates', 'accounts', 'deliverability', 'logs'].map(tab => (
          <button key={tab} className={activeTab === tab ? 'active' : ''} onClick={() => setActiveTab(tab)}>
            {tab.replace(/^./, letter => letter.toUpperCase())}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <section className="mail-section">
          <h3>Control Center</h3>
          <div className="overview-grid">
            <div><h4>Provider</h4><p>{smtpConfig.host || 'No SMTP host'} as {smtpConfig.from || 'No sender'}</p><p>Daily guard: {emailConfig?.sendingLimits?.dailyLimit || 200} messages.</p></div>
            <div><h4>Mailjet Webhook</h4><p>Delivery events can post to:</p><code>https://creativetown.ng/api/admin/email/mailjet/webhook</code></div>
            <div><h4>Workflows</h4><p>Compose, reply, forward, bulk, schedule, draft, suppress, export, audit, and DNS health checks are available here.</p></div>
          </div>
        </section>
      )}

      {activeTab === 'compose' && (
        <section className="mail-section">
          <Composer title="Compose Mail" model={compose} setModel={setCompose} senderOptions={senderOptions} templates={templates} signatures={signatures} groupOptions={groupOptions} files={files} setFiles={setFiles} recipientCount={recipientCount(compose)} />
          <div className="compose-actions">
            <button disabled={loading} onClick={() => sendCompose('send')}>Send Now</button>
            <button disabled={loading} onClick={() => sendCompose('draft')}>Save Draft</button>
            <button disabled={loading || !compose.sendAt} onClick={() => sendCompose('schedule')}>Schedule</button>
          </div>
        </section>
      )}

      {activeTab === 'mailbox' && (
        <section className="mail-section mailbox-layout">
          <div className="mailbox-toolbar">
            <select value={folder} onChange={event => { setFolder(event.target.value); loadMailbox(event.target.value, search); }}>
              <option value="all">All</option><option value="received">Inbox</option><option value="sent">Sent</option><option value="draft">Drafts</option><option value="spam">Spam</option><option value="trash">Trash</option>
            </select>
            <input value={search} onChange={event => setSearch(event.target.value)} onKeyDown={event => event.key === 'Enter' && loadMailbox(folder, search)} placeholder="Search mail" />
            <button onClick={() => loadMailbox(folder, search)}>Search</button>
          </div>
          <div className="mailbox-grid">
            <div className="mail-list">
              {mailbox.map(mail => (
                <button key={mail.id} className={`mail-row ${selectedMail?.id === mail.id ? 'active' : ''}`} onClick={() => setSelectedMail(mail)}>
                  <b>{mail.subject || 'No subject'}</b>
                  <span>{Array.isArray(mail.to) ? mail.to.join(', ') : mail.to}</span>
                  <small>{new Date(mail.createdAt).toLocaleString()} - {mail.deliveryStatus || mail.status}</small>
                </button>
              ))}
              {!mailbox.length && <p>No mail found.</p>}
            </div>
            <div className="mail-preview">
              {selectedMail ? (
                <>
                  <h3>{selectedMail.subject}</h3>
                  <p><b>From:</b> {selectedMail.from}</p>
                  <p><b>To:</b> {Array.isArray(selectedMail.to) ? selectedMail.to.join(', ') : selectedMail.to}</p>
                  <p><b>Delivery:</b> {selectedMail.deliveryStatus || (selectedMail.delivery?.success ? selectedMail.delivery.provider : selectedMail.delivery?.error) || selectedMail.status}</p>
                  <div className="mail-body">{selectedMail.text}</div>
                  {!!selectedMail.attachments?.length && <div className="attachment-list">{selectedMail.attachments.map(file => <a key={file.url} href={file.url}>{file.originalName}</a>)}</div>}
                  <div className="preview-actions">
                    <button onClick={() => { setCompose({ ...emptyCompose, from: smtpConfig.from, to: selectedMail.from, subject: `Re: ${selectedMail.subject}`, body: `\n\nOriginal message:\n${selectedMail.text || ''}` }); setActiveTab('compose'); }}>Reply</button>
                    <button onClick={() => { setCompose({ ...emptyCompose, from: smtpConfig.from, subject: `Fwd: ${selectedMail.subject}`, body: `\n\nForwarded message:\n${selectedMail.text || ''}` }); setActiveTab('compose'); }}>Forward</button>
                    <button onClick={() => updateMail(selectedMail, { read: true })}>Mark Read</button>
                    <button onClick={() => updateMail(selectedMail, { folder: 'spam' })}>Spam</button>
                    <button className="danger" onClick={() => updateMail(selectedMail, { folder: 'trash' })}>Trash</button>
                  </div>
                </>
              ) : <p>Select a message to read it.</p>}
            </div>
          </div>
        </section>
      )}

      {activeTab === 'bulk' && (
        <section className="mail-section">
          <Composer title="Bulk Email" model={bulk} setModel={setBulk} senderOptions={senderOptions} templates={templates} signatures={signatures} groupOptions={groupOptions} files={bulkFiles} setFiles={setBulkFiles} recipientCount={recipientCount(bulk)} bulk />
          <div className="compose-actions">
            <button disabled={loading} onClick={() => sendBulk('send')}>Send Bulk</button>
            <button disabled={loading || !bulk.sendAt} onClick={() => sendBulk('schedule')}>Schedule Bulk</button>
          </div>
        </section>
      )}

      {activeTab === 'templates' && (
        <section className="mail-section">
          <h3>Templates, Signatures, Contacts</h3>
          <div className="template-grid">
            {Object.entries(templates).map(([key, template]) => (
              <div className="template-item" key={key}>
                <h4>{key.replace(/([A-Z])/g, ' $1')}</h4>
                <input value={template.subject || ''} onChange={event => setTemplates({ ...templates, [key]: { ...template, subject: event.target.value } })} placeholder="Subject" />
                <textarea value={template.body || ''} onChange={event => setTemplates({ ...templates, [key]: { ...template, body: event.target.value } })} placeholder="Template body with {variables}" />
                <label><input type="checkbox" checked={Boolean(template.enabled)} onChange={event => setTemplates({ ...templates, [key]: { ...template, enabled: event.target.checked } })} /> Enabled</label>
              </div>
            ))}
          </div>
          <button onClick={saveTemplates}>Save Templates</button>
          <div className="split-grid">
            <div>
              <h4>Email Signatures</h4>
              <input value={signatureForm.name} onChange={event => setSignatureForm({ ...signatureForm, name: event.target.value })} placeholder="Signature name" />
              <textarea value={signatureForm.body} onChange={event => setSignatureForm({ ...signatureForm, body: event.target.value })} />
              <label><input type="checkbox" checked={signatureForm.defaultSignature} onChange={event => setSignatureForm({ ...signatureForm, defaultSignature: event.target.checked })} /> Default</label>
              <button onClick={saveSignature}>Save Signature</button>
              {signatures.map(signature => <p key={signature.id}>{signature.name} <button onClick={() => deleteSignature(signature.id)}>Delete</button></p>)}
            </div>
            <div>
              <h4>Contacts</h4>
              <input value={contactForm.name} onChange={event => setContactForm({ ...contactForm, name: event.target.value })} placeholder="Name" />
              <input value={contactForm.email} onChange={event => setContactForm({ ...contactForm, email: event.target.value })} placeholder="email@example.com" />
              <input value={contactForm.type} onChange={event => setContactForm({ ...contactForm, type: event.target.value })} placeholder="Type" />
              <button onClick={saveContact}>Save Contact</button>
              <p>{recipients.people?.length || 0} recipients available.</p>
            </div>
          </div>
        </section>
      )}

      {activeTab === 'accounts' && (
        <section className="mail-section">
          <h3>@creativetown.ng Accounts</h3>
          <div className="account-form">
            <select value={accountForm.ownerType} onChange={event => setAccountForm({ ...accountForm, ownerType: event.target.value })}><option value="student">Student</option><option value="parent">Parent</option><option value="teacher">Teacher</option><option value="non_academic">Non-Academic Staff</option><option value="security">Security</option><option value="admin">Admin</option></select>
            <input value={accountForm.displayName} onChange={event => setAccountForm({ ...accountForm, displayName: event.target.value })} placeholder="Display name" />
            <input value={accountForm.ownerId} onChange={event => setAccountForm({ ...accountForm, ownerId: event.target.value })} placeholder="Owner ID" />
            <input value={accountForm.localPart} onChange={event => setAccountForm({ ...accountForm, localPart: event.target.value })} placeholder="name before @creativetown.ng" />
            <label><input type="checkbox" checked={accountForm.dashboardAccess} onChange={event => setAccountForm({ ...accountForm, dashboardAccess: event.target.checked })} /> Dashboard mail access</label>
            <button onClick={createAccount}>Add Account</button>
          </div>
          <div className="accounts-list">
            {accounts.map(account => (
              <div key={account.id} className="account-card">
                <div><h4>{account.displayName}</h4><p>{account.email}</p><span>{account.ownerType} {account.ownerId}</span></div>
                <div className="account-actions"><button onClick={() => updateAccount(account, { active: !account.active })}>{account.active ? 'Disable' : 'Enable'}</button><button onClick={() => updateAccount(account, { dashboardAccess: !account.dashboardAccess })}>{account.dashboardAccess ? 'Remove Access' : 'Allow Access'}</button><button className="danger" onClick={() => deleteAccount(account)}>Delete</button></div>
              </div>
            ))}
          </div>
        </section>
      )}

      {activeTab === 'deliverability' && (
        <section className="mail-section">
          <h3>Deliverability</h3>
          <div className="config-grid">
            <div>
              <h4>SMTP</h4>
              <input value={smtpConfig.host} onChange={event => setSmtpConfig({ ...smtpConfig, host: event.target.value })} placeholder="SMTP host" />
              <input type="number" value={smtpConfig.port} onChange={event => setSmtpConfig({ ...smtpConfig, port: Number(event.target.value) })} />
              <input value={smtpConfig.user} onChange={event => setSmtpConfig({ ...smtpConfig, user: event.target.value })} placeholder="Username" />
              <input type="password" value={smtpConfig.pass} onChange={event => setSmtpConfig({ ...smtpConfig, pass: event.target.value })} placeholder="New password only" />
              <input value={smtpConfig.from} onChange={event => setSmtpConfig({ ...smtpConfig, from: event.target.value })} placeholder="From email" />
              <input value={smtpConfig.fromName} onChange={event => setSmtpConfig({ ...smtpConfig, fromName: event.target.value })} placeholder="From name" />
              <label><input type="checkbox" checked={smtpConfig.secure} onChange={event => setSmtpConfig({ ...smtpConfig, secure: event.target.checked })} /> SSL/TLS</label>
              <button onClick={saveSmtp}>Save SMTP</button>
            </div>
            <div>
              <h4>SendGrid Fallback</h4>
              <label><input type="checkbox" checked={sendgridConfig.enabled} onChange={event => setSendgridConfig({ ...sendgridConfig, enabled: event.target.checked })} /> Enabled</label>
              <input type="password" value={sendgridConfig.apiKey} onChange={event => setSendgridConfig({ ...sendgridConfig, apiKey: event.target.value })} placeholder="New API key only" />
              <button onClick={saveSendgrid}>Save SendGrid</button>
              <h4>DNS Health</h4>
              <button onClick={runDnsHealth}>Run DNS Check</button>
              {dnsHealth?.checks?.map(check => <div key={check.name} className={`dns-row ${check.ok ? 'ok' : 'bad'}`}><b>{check.name}: {check.ok ? 'OK' : 'Needs work'}</b><p>{check.value}</p><small>{check.recommendation}</small></div>)}
            </div>
          </div>
          <div className="split-grid">
            <div>
              <h4>Suppression List</h4>
              <input value={suppressForm.email} onChange={event => setSuppressForm({ ...suppressForm, email: event.target.value })} placeholder="bad@example.com" />
              <input value={suppressForm.reason} onChange={event => setSuppressForm({ ...suppressForm, reason: event.target.value })} placeholder="Reason" />
              <button onClick={addSuppression}>Suppress</button>
              {suppression.map(item => <p key={item.email || item}>{item.email || item} <button onClick={() => removeSuppression(item.email || item)}>Remove</button></p>)}
            </div>
            <div>
              <h4>Scheduled Mail</h4>
              {scheduled.map(item => <p key={item.id}>{new Date(item.sendAt).toLocaleString()} - {item.status} <button onClick={() => cancelScheduled(item)}>Cancel</button></p>)}
              {!scheduled.length && <p>No scheduled mail.</p>}
            </div>
          </div>
        </section>
      )}

      {activeTab === 'logs' && (
        <section className="mail-section">
          <h3>Logs and Audit Trail</h3>
          <div className="logs-table">
            <table><thead><tr><th>Time</th><th>Type</th><th>To</th><th>Subject</th><th>Status</th><th>Provider</th></tr></thead><tbody>{logs.map((log, index) => <tr key={`${log.timestamp}-${index}`}><td>{new Date(log.timestamp).toLocaleString()}</td><td>{log.type}</td><td>{log.to}</td><td>{log.subject}</td><td className={log.success ? 'success' : 'error'}>{log.success ? 'Success' : 'Failed'}</td><td>{log.provider || '-'}</td></tr>)}</tbody></table>
          </div>
          <h4>Audit</h4>
          <div className="audit-list">{audit.map(item => <p key={item.id}>{new Date(item.createdAt).toLocaleString()} - {item.action}</p>)}</div>
        </section>
      )}
    </div>
  );
};

const Composer = ({ title, model, setModel, senderOptions, templates, signatures, groupOptions, files, setFiles, recipientCount, bulk }) => (
  <div className="composer">
    <h3>{title}</h3>
    <div className="form-grid">
      <select value={model.from} onChange={event => setModel({ ...model, from: event.target.value })}><option value="">Choose sender</option>{senderOptions.map(sender => <option key={sender.email} value={sender.email}>{sender.displayName || sender.email} - {sender.email}</option>)}</select>
      <select value={model.templateKey} onChange={event => setModel({ ...model, templateKey: event.target.value })}><option value="">No template</option>{Object.keys(templates).map(key => <option key={key} value={key}>{key}</option>)}</select>
      <select value={model.signatureId} onChange={event => setModel({ ...model, signatureId: event.target.value })}><option value="">Default signature</option>{signatures.map(signature => <option key={signature.id} value={signature.id}>{signature.name}</option>)}</select>
      <select value={model.type} onChange={event => setModel({ ...model, type: event.target.value })}><option value="compose">General</option><option value="announcement">Announcement</option><option value="newsletter">Newsletter</option><option value="reminder">Reminder</option><option value="notification">Notification</option></select>
    </div>
    {bulk && <div className="group-picker">{groupOptions.map(group => <label key={group}><input type="checkbox" checked={model.groups.includes(group)} onChange={event => setModel({ ...model, groups: event.target.checked ? [...model.groups, group] : model.groups.filter(item => item !== group) })} />{group}</label>)}</div>}
    <input value={model.to} onChange={event => setModel({ ...model, to: event.target.value })} placeholder={bulk ? 'Extra recipients, comma-separated' : 'To'} />
    <div className="form-grid"><input value={model.cc} onChange={event => setModel({ ...model, cc: event.target.value })} placeholder="Cc" /><input value={model.bcc} onChange={event => setModel({ ...model, bcc: event.target.value })} placeholder="Bcc" /></div>
    <input value={model.subject} onChange={event => setModel({ ...model, subject: event.target.value })} placeholder="Subject" />
    <textarea value={model.body} onChange={event => setModel({ ...model, body: event.target.value })} placeholder="Write the Creative Town mail body here. Use {studentName}, {className}, {amount}, or custom variables." />
    <div className="form-grid"><input value={model.ctaText} onChange={event => setModel({ ...model, ctaText: event.target.value })} placeholder="Button text" /><input value={model.ctaUrl} onChange={event => setModel({ ...model, ctaUrl: event.target.value })} placeholder="Button URL" /><input type="datetime-local" value={model.sendAt} onChange={event => setModel({ ...model, sendAt: event.target.value })} /><input type="file" multiple onChange={event => setFiles([...event.target.files])} /></div>
    <div className="compose-meta"><span>{recipientCount} recipient{recipientCount === 1 ? '' : 's'}</span><span>{files.length} attachment{files.length === 1 ? '' : 's'}</span></div>
  </div>
);

export default AdminEmailManagement;
